import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useState,
} from "react";
import instance from "../../Interceptor/api_instance";
import CheckInOutForm from "./CheckInVehicleForm";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../Auth/AuthProvider";
import CheckInTruckF15 from "./CheckInTruckF15";
import CheckInTrailerF15 from "./CheckInTrailerF15";
import CheckInPhotos from "./CheckInPhotos";
import CheckoutConfirm from "../../Components/Modals/CheckoutConfirm";
import {
  transformCheckInTrailer,
  transformCheckInTruck,
} from "./transformData";
import { ActionResponse } from "../../Components/Modals/ActionResponses";

const reducer = (state, action) => {
  switch (action.type) {
    case "IS_CARRYING":
      return {
        ...state,
        isCarrying: action.payload,
      };
    case "IS_TRAILER_ATI":
      return {
        ...state,
        isTrailerAti: action.payload,
      };
    case "SELECT_CARRIER":
      return {
        ...state,
        selectedCarrier: action.payload,
      };
    case "IS_TRAILER_SCANNING":
      return {
        ...state,
        trailerCodeScanner: action.payload,
      };
    case "IS_TRUCK_SCANNING":
      return {
        ...state,
        truckCodeScanner: action.payload,
      };
    case "ERROR_MESSAGE":
      return {
        ...state,
        [action.key]: action.payload,
      };
    case "STORE_TRUCKS":
      return {
        ...state,
        jfgTruckList: action.jfgList,
        atiTruckList: action.atiList,
      };
    case "STORE_DRIVERS":
      return {
        ...state,
        driverList: action.payload,
      };
    case "SET_TRAILER_DIMENSION":
      return {
        ...state,
        trailerDimension: action.payload,
      };
    case "STORE_TRAILERS":
      return {
        ...state,
        trailerList: action.payload,
      };
    case "STORE_CUSTOMERS":
      return {
        ...state,
        customerList: action.payload,
      };
    case "ERROR":
      return {
        ...state,
        error: action.error,
      };
    case "PAGE_NUM":
      return {
        ...state,
        pageNum: action.payload,
      };
    case "CONFIRM":
      return {
        ...state,
        confirm: action.payload,
      };
    case "SUBMIT_REQUEST":
      return {
        ...state,
        submitLoading: true,
      };
    case "SUBMIT_COMPLETE":
      return {
        ...state,
        submitLoading: false,
        message: action.message,
        status: action.status,
      };
    default:
      return state;
  }
};

const initialState = {
  isCarrying: null,
  isTrailerAti: null,
  isCarrierOther: false,
  isLoaded: null,
  isSealed: null,
  jfgTruckList: [],
  atiTruckList: [],
  trailerList: [],
  pageNum: 1,
  selectedCarrier: "",
  trailerCodeScanner: false,
  trailerErrorMessage: null,
  truckCodeScanner: false,
  truckErrorMessage: null,
  error: null,
  trailerDimension: null,
  customerList: [],
  submitLoading: false,
  confirm: false,
  status: null,
  message: null,
  driverList: [],
};

export const CheckInVehicle = ({ onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check if any previous data is stored in cache
  const formStateCache = JSON.parse(localStorage.getItem("formState"));
  const trailerF15Cache = JSON.parse(localStorage.getItem("trailerForm"));
  const truckF15Cache = JSON.parse(localStorage.getItem("truckForm"));

  // Get the form data from localStorage, if it exists
  const checkInFormDataCache = JSON.parse(
    localStorage.getItem("checkInFormData"),
  );

  const [formState, dispatchForm] = useReducer(
    reducer,
    formStateCache || initialState,
  );

  const [checkInFormData, setCheckInFormData] = useState(
    checkInFormDataCache || {},
  );

  const [capturedPhotos, setCapturedPhotos] = useState([]);
  const [pinNum, setPinNum] = useState("");
  const [f15TruckBody, setF15TruckBody] = useState(
    truckF15Cache ||
      (() => {
        const defaultStructure = {
          notes: "",
        };

        const keys = [
          "chassis",
          "bumper",
          "engine",
          "tires",
          "cabFloor",
          "ceiling",
          "fuelTank",
          "compartments",
          "airTank",
          "driveShafts",
          "fifthWheel",
          "exhaust",
          "pests",
          "garbage",
          "cleanliness",
        ];

        const form = {
          truck_inspection: {},
          anomalies: null,
        };

        // Create the truckForm object with the same structure for all keys
        keys.forEach((key) => {
          form.truck_inspection[key] = { ...defaultStructure };
        });
        return form;
      }),
  );
  const [f15TrailerBody, setF15TrailerBody] = useState(
    trailerF15Cache ||
      (() => {
        const defaultStructure = {
          notes: "",
        };

        const keys = [
          "chassis",
          "inOutDoors",
          "trailerFloor",
          "tires",
          "sideWalls",
          "frontWall",
          "ceiling",
          "pests",
          "garbage",
          "cleanliness",
          "filter",
          "humidity",
        ];

        const form = {
          trailer_inspection: {},
          seal_tests: {
            view_seal: null,
            verify_seal: null,
            tug_on_seal: null,
            twistTurn_seal: null,
          },
          anomalies: null,
        };

        // Assign default structure to keys with value/notes structure
        keys.forEach((key) => {
          form.trailer_inspection[key] = { ...defaultStructure };
        });

        form.trailer_inspection["sealCondition"] = { value: "", notes: "" };
        form.trailer_inspection["refrigerator"] = { value: "", notes: "" };

        return form;
      }),
  );

  if (checkInFormData.trailerNumber?.toLowerCase()?.startsWith("lsf")) {
    console.log("Trailer starts with LSF, setting  to true");
  }
  useLayoutEffect(() => {
    const el = document.scrollingElement || document.documentElement;
    // temporarily defeat smooth and anchoring effects
    document.documentElement.style.overflowAnchor = "none";
    el.style.scrollBehavior = "auto";

    el.scrollTo(0, 0);
    requestAnimationFrame(() => {
      el.scrollTo(0, 0);
      // restore
      document.documentElement.style.overflowAnchor = "";
      el.style.scrollBehavior = "";
    });
  }, [formState.pageNum]);

  const nextForm = useCallback(() => {
    if (user.location.loc_country === "Mexico") {
      // Mexico
      // This handles the F15 navigation
      if (formState.isCarrying && formState.pageNum === 2) {
        dispatchForm({ type: "PAGE_NUM", payload: formState.pageNum + 1 });
      } else if (!formState.isCarrying && formState.pageNum === 2) {
        dispatchForm({ type: "PAGE_NUM", payload: formState.pageNum + 2 });
      } else {
        dispatchForm({ type: "PAGE_NUM", payload: formState.pageNum + 1 });
      }
    }
    // Trailer number starts with "LSF" which indicates it's a trailer Living Spaces, so we need to show the F15 trailer form regardless of the country
    else if (
      checkInFormData.trailerNumber &&
      checkInFormData.trailerNumber.toLowerCase().startsWith("lsf")
    ) {
      console.log("Trailer starts with LSF, showing F15 trailer form");
      if (formState.pageNum === 1) {
        dispatchForm({ type: "PAGE_NUM", payload: formState.pageNum + 2 });
      }
      else {
        dispatchForm({ type: "PAGE_NUM", payload: formState.pageNum + 1 });
      }
    } else {
      // Skips over the F15 forms
      dispatchForm({ type: "PAGE_NUM", payload: 4 });
    }
  }, [
    dispatchForm,
    formState.isCarrying,
    formState.pageNum,
    user.location.loc_country,
    checkInFormData.trailerNumber,
  ]);

  const backForm = useCallback(() => {
    if (user.location.loc_country === "Mexico") {
      // Mexico
      // This handles the F15 navigation
      if (!formState.isCarrying && formState.pageNum === 4) {
        dispatchForm({ type: "PAGE_NUM", payload: formState.pageNum - 2 });
      } else {
        dispatchForm({ type: "PAGE_NUM", payload: formState.pageNum - 1 });
      }
    } else if (
      checkInFormData.trailerNumber &&
      checkInFormData.trailerNumber.toLowerCase().startsWith("lsf")
    ) {
      if (formState.pageNum === 4) {
        dispatchForm({ type: "PAGE_NUM", payload: formState.pageNum - 1 });
      }
      else {
        dispatchForm({type: "PAGE_NUM", payload: formState.pageNum - 2 });
      }
    } else {
      dispatchForm({ type: "PAGE_NUM", payload: 1 }); // Skips over the F15 forms
    }
  }, [
    dispatchForm,
    formState.isCarrying,
    formState.pageNum,
    user.location.loc_country,
    checkInFormData.trailerNumber,
  ]);

  const submit = async (e) => {
    e.preventDefault();
    dispatchForm({ type: "SUBMIT_REQUEST" });
    const formData = new FormData();
    capturedPhotos.forEach((photo) => {
      formData.append("photo", photo);
    });
    try {
      if (formState.isCarrying) {
        const data = {
          ...transformCheckInTrailer(checkInFormData),
          f15Trailer: { ...f15TrailerBody },
          f15Truck: { ...f15TruckBody },
          reason: "Checking in a trailer",
          employee_pin: pinNum,
        };
        formData.append("data", JSON.stringify(data));
        const checkInTrailer = await instance.post(
          "assets/check-in-trailer/",
          formData,
          {
            headers: {
              "Content-Type": "multipart/data-form",
            },
          },
        );
        if (checkInTrailer.status === 200) {
          dispatchForm({
            type: "SUBMIT_COMPLETE",
            message: t("forms.responses.success"),
            status: t("forms.type.success"),
          });
        }
      } else {
        const data = {
          ...transformCheckInTruck(checkInFormData),
          f15Truck: { ...f15TruckBody },
          reason: "Checking in a truck",
          employee_pin: pinNum,
        };
        formData.append("data", JSON.stringify(data));
        const checkInTruck = await instance.post(
          "assets/check-in-truck/",
          formData,
          {
            headers: {
              "Content-Type": "multipart/data-form",
            },
          },
        );
        if (checkInTruck.status === 200) {
          dispatchForm({
            type: "SUBMIT_COMPLETE",
            message: t("forms.responses.success"),
            status: t("forms.type.success"),
          });
        }
      }
      localStorage.removeItem("truckBody");
      localStorage.removeItem("formState");
      localStorage.removeItem("truckForm");
      localStorage.removeItem("trailerForm");
      localStorage.removeItem("checkInFormData");
    } catch (error) {
      console.error(error);
      dispatchForm({
        type: "SUBMIT_COMPLETE",
        message: error,
        status: "Error",
      });
    }
  };

  return (
    <div>
      {formState.pageNum === 1 && (
        <CheckInOutForm
          formState={formState}
          dispatchForm={dispatchForm}
          formData={checkInFormData}
          setFormData={setCheckInFormData}
          close={onClose}
          nextForm={nextForm}
        />
      )}
      {formState.pageNum === 2 && (
        <CheckInTruckF15
          nextForm={nextForm}
          backForm={backForm}
          truckForm={f15TruckBody}
          setTruckForm={setF15TruckBody}
          inOrOut={true}
        />
      )}
      {formState.pageNum === 3 && (
        <CheckInTrailerF15
          nextForm={nextForm}
          backForm={backForm}
          trailerForm={f15TrailerBody}
          setTrailerForm={setF15TrailerBody}
          sealStatus={checkInFormData.sealNumber}
          inOrOut={true}
        />
      )}
      {formState.pageNum === 4 && (
        <CheckInPhotos
          backForm={backForm}
          capturedPhotos={capturedPhotos}
          setCapturedPhotos={setCapturedPhotos}
          confirm={() => dispatchForm({ type: "CONFIRM", payload: true })}
          pinNum={pinNum}
          setPinNum={setPinNum}
          requestLoading={formState.submitLoading}
          inOrOut={true}
        />
      )}
      {formState.confirm && (
        <CheckoutConfirm
          type="check in"
          requestLoading={formState.submitLoading}
          truck={
            checkInFormData.truckAsset
              ? checkInFormData.truckNumber
              : `${checkInFormData.carrierName} ${checkInFormData.truckNumber}`
          }
          trailer={
            checkInFormData.trailer_asset
              ? checkInFormData.trailer_asset.unit
              : checkInFormData.trailerNumber
          }
          close={() => dispatchForm({ type: "CONFIRM", payload: false })}
          submit={submit}
        />
      )}
      {formState.message && (
        <ActionResponse
          message={formState.message}
          status={formState.status}
          close={
            formState.status === t("forms.type.success")
              ? () => onClose()
              : () =>
                  dispatchForm({
                    type: "SUBMIT_COMPLETE",
                    message: null,
                    status: null,
                  })
          }
        />
      )}
    </div>
  );
};
